/**
 * Stores (Storage & Database) API client — wraps the `/datasets/*` routes
 * served by the backend, base-path mapped onto api-${stage}.zibby.app.
 *
 *   list() → { ok, stores: [{ storeId, name, type, description, schema,
 *                              status, createdAt }], canWrite }
 *
 * The `/datasets` registry is PROJECT-TOKEN scoped (it's the same API the
 * cloud agent + Copilot call) — it rejects the user session token. So the
 * caller passes the current project's `apiToken` (already delivered to the
 * browser via GET /projects) and we send it as the per-call Authorization
 * override. The token also pins the tenant server-side, so there's no
 * projectId argument.
 *
 * Stores are CREATED conversationally (via the agent / Copilot) — no create
 * client here. The browse surface is read-only EXCEPT the two deliberate
 * mutations the store detail page owns: sqlite inline cell edit (sqlWrite,
 * a bound prepared statement the backend gates) and store deletion (remove,
 * which requires an explicit confirm and purges the data server-side).
 */

import api from './api';

const BASE = '/datasets';

// File/docs listing is PAGINATED server-side (`nextCursor`, max 1000 keys per
// call). Rendering one page as the whole store is a SILENT truncation, so
// listAllFiles follows the cursor to the end — and when a store is genuinely
// bigger than this ceiling it reports `truncated: true` so the UI can say the
// list is partial instead of quietly cutting it off.
const LIST_CAP = 5000;

export const Stores = {
  /**
   * List the project's stores (databases / data stores).
   * @param {string} projectToken - the project's apiToken (the /datasets
   *   registry authenticates with the project token, not the session token).
   * @param {{ withSize?: boolean }} [opts] - withSize opts into the live
   *   per-store size probe (slow — never on a first-paint path).
   * @returns {Promise<{ ok: boolean, stores: Array }>}
   */
  list(projectToken, opts) {
    const q = opts && opts.withSize ? '?withSize=1' : '';
    return api.get(`${BASE}/stores${q}`, { authToken: projectToken });
  },

  /**
   * ONE store by id — the read the detail page makes. `withSize` runs the live
   * size/docCount probe for THIS store only, so it's a separate, non-blocking
   * second call after the page has painted.
   * @returns {Promise<{ ok, store, canWrite?, noDataAccess? }>}
   */
  get(projectToken, storeId, opts) {
    const q = opts && opts.withSize ? '?withSize=1' : '';
    return api.get(`${BASE}/stores/${encodeURIComponent(storeId)}${q}`, { authToken: projectToken });
  },

  /** Delete a store AND its data (server purges). Confirm is explicit. */
  remove(projectToken, storeId) {
    return api.delete(`${BASE}/stores/${encodeURIComponent(storeId)}`, {
      body: { confirm: true },
      authToken: projectToken,
    });
  },

  // ── Browse endpoints (the store data viewer) ────────────────────────────────
  // All bounded + tenant-forced server-side.
  /** SQLite: read-only SQL (SELECT/PRAGMA/EXPLAIN only, enforced server-side). */
  sql(projectToken, storeId, sql) {
    return api.post(`${BASE}/stores/${encodeURIComponent(storeId)}/sql`, { sql, readOnly: true }, { authToken: projectToken });
  },
  /**
   * SQLite WRITE — a bound prepared statement (params), NOT read-only, so the
   * backend gates it (self-host: the member's dataWrite permission). Used only
   * by the detail page's inline cell editing.
   */
  sqlWrite(projectToken, storeId, sql, params) {
    return api.post(`${BASE}/stores/${encodeURIComponent(storeId)}/sql`, { sql, params }, { authToken: projectToken });
  },
  /** Dataset: structured query DSL (omit select → all columns). */
  query(projectToken, storeId, body) {
    return api.post(`${BASE}/stores/${encodeURIComponent(storeId)}/query`, body, { authToken: projectToken });
  },
  /** File store: list one page of paths (server caps at 1000/page). */
  listFiles(projectToken, storeId, body) {
    const b = typeof body === 'number' ? { limit: body } : (body || { limit: 50 });
    return api.post(`${BASE}/stores/${encodeURIComponent(storeId)}/list`, b, { authToken: projectToken });
  },
  /**
   * File store: the COMPLETE path list — follows `nextCursor` to the end (cap
   * LIST_CAP), so callers never mistake page one for the whole store.
   * @returns {Promise<{ files: Array<{path,size}>, truncated: boolean }>}
   */
  async listAllFiles(projectToken, storeId, cap = LIST_CAP) {
    let files = [];
    let cursor = '';
    for (;;) {
      const body = cursor ? { limit: 1000, cursor } : { limit: 1000 };
      const r = await Stores.listFiles(projectToken, storeId, body);
      files = files.concat((r && r.files) || []);
      if (files.length >= cap) return { files: files.slice(0, cap), truncated: true };
      if (r && r.nextCursor) { cursor = r.nextCursor; continue; }
      return { files, truncated: false };
    }
  },
  /** File store: fetch one file's content. */
  getFile(projectToken, storeId, path) {
    return api.post(`${BASE}/stores/${encodeURIComponent(storeId)}/get`, { path }, { authToken: projectToken });
  },
};

export default Stores;
